import { useLiveQuery } from 'dexie-react-hooks'
import Decimal from 'decimal.js'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { fixerCaution, fixerReception, libererRetenue, listerChantiers } from '../lib/chantiers'
import { eurosVersCentimes, formatEuro, saisieEuros } from '../lib/money'
import { aujourdhuiLocal } from '../lib/planning'
import { cautionsASurveiller, etatCaution, LIBELLES_ETAT_CAUTION, LIBELLES_ETAT_RETENUE, retenuesDeGarantie } from '../lib/retenue'
import { situationsDuDevis } from '../lib/situations'
import { getBase, type ChantierLocal, type SituationLocal } from '../outbox/base'
import { Badge } from './composants/badge'
import { Bouton } from './composants/bouton'
import { CHAMP } from './synchro'

const dateFr = (j: string) => j.split('-').reverse().join('/')

/** Retenues de garantie suivies, calculées sur les situations finalisées du devis de chaque chantier. */
function useRetenues(chantiers: ChantierLocal[]) {
  const base = getBase()
  const situations = useLiveQuery(async (): Promise<Record<string, SituationLocal[]>> => Object.fromEntries(await Promise.all(chantiers.filter((c) => c.devisId).map(async (c) => [c.devisId!, await situationsDuDevis(base, c.devisId!)] as const))), [chantiers.map((c) => c.devisId).join()], {} as Record<string, SituationLocal[]>)
  return retenuesDeGarantie(chantiers, (devisId) => situations[devisId] ?? [], aujourdhuiLocal())
}

/** Fiche du chantier : réception des travaux et libération de la retenue de garantie. */
export function BlocReception({ chantier }: { chantier: ChantierLocal }) {
  const base = getBase()
  const retenue = useRetenues([chantier])[0]
  const [erreur, setErreur] = useState<string | null>(null)
  const [jourLiberation, setJourLiberation] = useState(aujourdhuiLocal())
  const essayer = async (f: () => Promise<unknown>) => {
    setErreur(null)
    try {
      await f()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible.')
    }
  }

  return (
    <section aria-label="Réception et retenue de garantie" className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white shadow-sm p-3">
      <h3 className="font-semibold">Réception et retenue de garantie</h3>
      <div className="grid grid-cols-2 items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-slate-700">Réception des travaux</span>
          <input type="date" className={CHAMP} value={chantier.receptionLe ?? ''} max={aujourdhuiLocal()} onChange={(e) => essayer(() => fixerReception(base, chantier.id, e.target.value || undefined, !!chantier.reserves))} />
        </label>
        <label className="flex min-h-12 items-center gap-2 text-sm">
          <input type="checkbox" className="size-5" checked={!!chantier.reserves} disabled={!chantier.receptionLe} onChange={(e) => essayer(() => fixerReception(base, chantier.id, chantier.receptionLe, e.target.checked))} />
          Réserves non levées
        </label>
      </div>
      {!chantier.devisId && <p className="text-sm text-slate-700">Chantier libre : pas de situations de travaux, donc pas de retenue à suivre.</p>}
      {chantier.devisId && chantier.receptionLe && !retenue && <p className="text-sm text-slate-700">Aucune retenue de garantie prélevée à ce jour sur les situations finalisées.</p>}
      {chantier.devisId && !chantier.receptionLe && <p className="text-sm text-slate-700">Saisissez la date de réception : la retenue de garantie se libère un an après, sauf réserves non levées.</p>}
      {retenue && (
        <div className="flex flex-col gap-2 text-sm">
          <p>
            Retenue prélevée : <strong>{formatEuro(retenue.montantTtc)}</strong> TTC. Libérable à partir du <strong>{dateFr(retenue.echeance)}</strong>.
          </p>
          <Badge statut={retenue.etat === 'liberee' ? 'succes' : retenue.etat === 'echue' ? 'danger' : retenue.etat === 'reserves' ? 'neutre' : 'info'} className="self-start">
            {LIBELLES_ETAT_RETENUE[retenue.etat]}
            {retenue.etat === 'liberee' && ` le ${dateFr(chantier.retenueLibereeLe!)}`}
            {(retenue.etat === 'bientot' || retenue.etat === 'en_attente') && ` (dans ${retenue.joursRestants} j)`}
          </Badge>
          {chantier.retenueLibereeLe ? (
            <Bouton variante="lien" className="self-start" onClick={() => essayer(() => libererRetenue(base, chantier.id, undefined))}>
              Remettre la retenue en attente
            </Bouton>
          ) : (
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-slate-700">Retenue encaissée le</span>
                <input type="date" className={CHAMP} value={jourLiberation} min={chantier.receptionLe} max={aujourdhuiLocal()} onChange={(e) => setJourLiberation(e.target.value)} />
              </label>
              <Bouton variante="secondaire" onClick={() => essayer(() => libererRetenue(base, chantier.id, jourLiberation))}>
                Marquer la retenue libérée
              </Bouton>
            </div>
          )}
        </div>
      )}
      {erreur && (
        <p className="rounded border-2 border-red-700 bg-red-50 p-2 text-sm text-red-900" role="alert">
          {erreur}
        </p>
      )}
      <BlocCaution chantier={chantier} />
    </section>
  )
}

/** Caution bancaire qui remplace la retenue : saisie et état de validité. */
function BlocCaution({ chantier }: { chantier: ChantierLocal }) {
  const base = getBase()
  const c = chantier.caution
  const [edition, setEdition] = useState(false)
  const [montant, setMontant] = useState(c ? saisieEuros(c.montantCents) : '')
  const [banque, setBanque] = useState(c?.banque ?? '')
  const [reference, setReference] = useState(c?.reference ?? '')
  const [expireLe, setExpireLe] = useState(c?.expireLe ?? '')
  const [erreur, setErreur] = useState<string | null>(null)
  const etat = c ? etatCaution(c, aujourdhuiLocal()) : null

  async function enregistrer() {
    setErreur(null)
    const cents = eurosVersCentimes(montant)
    if (cents === null || cents < 1) return setErreur('Montant illisible : écrivez par exemple 2 350 ou 2350,50.')
    try {
      await fixerCaution(base, chantier.id, { montantCents: cents, banque, reference, expireLe })
      setEdition(false)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible.')
    }
  }

  return (
    <div className="flex flex-col gap-2 border-t border-slate-200 pt-3 text-sm" aria-label="Caution bancaire" role="group">
      <h4 className="font-semibold">Caution bancaire</h4>
      {c && etat && !edition && (
        <>
          <p>
            <strong>{formatEuro(c.montantCents / 100)}</strong> · {c.banque}
            {c.reference && ` · n° ${c.reference}`}
            {c.expireLe && ` · valable jusqu’au ${dateFr(c.expireLe)}`}
          </p>
          <Badge statut={etat.etat === 'active' ? 'succes' : etat.etat === 'bientot' ? 'info' : 'danger'} className="self-start">
            {LIBELLES_ETAT_CAUTION[etat.etat]}
          </Badge>
          <p className="text-slate-700">Une caution remplace la retenue de garantie : réglez la retenue à 0 % sur les situations.</p>
          <div className="flex gap-4">
            <Bouton variante="lien" onClick={() => setEdition(true)}>
              Modifier la caution
            </Bouton>
            <Bouton variante="lienDanger" onClick={() => essayerRetirer()}>
              Retirer la caution
            </Bouton>
          </div>
        </>
      )}
      {!c && !edition && (
        <Bouton variante="lien" className="self-start" onClick={() => setEdition(true)}>
          Enregistrer une caution bancaire
        </Bouton>
      )}
      {edition && (
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-slate-700">Montant garanti (€)</span>
            <input className={CHAMP} inputMode="decimal" value={montant} onChange={(e) => setMontant(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-slate-700">Banque</span>
            <input className={CHAMP} value={banque} maxLength={100} onChange={(e) => setBanque(e.target.value)} />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-slate-700">Référence</span>
              <input className={CHAMP} value={reference} maxLength={60} onChange={(e) => setReference(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-slate-700">Valable jusqu’au</span>
              <input type="date" className={CHAMP} value={expireLe} onChange={(e) => setExpireLe(e.target.value)} />
            </label>
          </div>
          {erreur && (
            <p className="rounded border-2 border-red-700 bg-red-50 p-2 text-red-900" role="alert">
              {erreur}
            </p>
          )}
          <div className="flex gap-3">
            <Bouton onClick={enregistrer} disabled={!montant.trim() || !banque.trim()}>
              Enregistrer la caution
            </Bouton>
            <Bouton variante="secondaire" onClick={() => setEdition(false)}>
              Annuler
            </Bouton>
          </div>
        </div>
      )}
    </div>
  )

  function essayerRetirer() {
    if (confirm('Retirer la caution bancaire de ce chantier ?')) void fixerCaution(base, chantier.id, undefined)
  }
}

/** Accueil : retenues de garantie à réclamer ou bientôt libérables (rien tant qu'aucune réception n'est saisie). */
export function RappelRetenues() {
  const base = getBase()
  const chantiers = useLiveQuery(() => listerChantiers(base), [], [])
  const a = useRetenues(chantiers).filter((r) => r.etat === 'echue' || r.etat === 'bientot')
  const cautions = cautionsASurveiller(chantiers, aujourdhuiLocal())
  if (a.length === 0 && cautions.length === 0) return null
  const total = a.reduce((n, r) => n.plus(r.montantTtc), new Decimal(0))
  return (
    <section aria-label="Retenues de garantie" className="flex flex-col gap-2 rounded-2xl border-2 border-nuit bg-slate-100 p-3 text-sm">
      {a.length > 0 && (
        <div className="flex flex-col gap-1">
          <h3 className="font-semibold">
            Retenues de garantie à libérer ({a.length} · {formatEuro(total)} TTC)
          </h3>
          <ul className="flex flex-col gap-0.5">
            {a.map((r) => (
              <li key={r.chantierId}>
                <Link to={`/chantiers/${r.chantierId}`} className="underline">
                  {r.chantierNom}
                </Link>{' '}
                · {formatEuro(r.montantTtc)} · {r.etat === 'echue' ? `à réclamer depuis le ${dateFr(r.echeance)}` : `libérable le ${dateFr(r.echeance)} (dans ${r.joursRestants} j)`}
              </li>
            ))}
          </ul>
        </div>
      )}
      {cautions.length > 0 && (
        <div className="flex flex-col gap-1">
          <h3 className="font-semibold">Cautions bancaires à surveiller ({cautions.length})</h3>
          <ul className="flex flex-col gap-0.5">
            {cautions.map((c) => (
              <li key={c.chantierId}>
                <Link to={`/chantiers/${c.chantierId}`} className="underline">
                  {c.chantierNom}
                </Link>{' '}
                · {formatEuro(c.caution.montantCents / 100)} · {c.etat === 'expiree' ? `expirée depuis le ${dateFr(c.caution.expireLe!)}` : `échéance le ${dateFr(c.caution.expireLe!)} (dans ${c.joursRestants} j)`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
